import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineLockClosed } from 'react-icons/hi';
import { toast } from 'react-toastify';
import PageList from '../../templates/PageList';
import PageSearch from '../../molecules/PageSearch';
import { ATableHeader } from '../../organisms/ATable';
import RoutingRow from './routing/RoutingRow';
import communicationApi from '../../../services/communicationApi';
import { MessageComposition, Routing } from '../../../types/communication';

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'r-event', title: 'Quando acontece', field: 'event', canSort: false, width: '340px' },
    { id: 'r-targets', title: 'Envia', field: 'targets', canSort: false },
    { id: 'r-conditions', title: 'Condições', field: 'conditions', canSort: false, width: '100px' },
    { id: 'r-envs', title: 'Ambientes', field: 'environments', canSort: false, width: '160px' },
    { id: 'r-enabled', title: 'Status', field: 'enabled', canSort: false, width: '90px' },
];

const RoutingPageList = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<Routing[]>([]);
    const [metas, setMetas] = useState<MessageComposition[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [routings, metaPage] = await Promise.all([
                communicationApi.listRoutings({}),
                communicationApi.listMessageCompositions({ pageSize: 9999 }),
            ]);
            setRows(routings);
            setMetas(metaPage.list);
        } catch {
            toast.error('Falha ao carregar roteamentos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const term = search.trim().toLowerCase();
    const filteredRows = term
        ? rows.filter((r) => `${r.event} ${r.ruleId}`.toLowerCase().includes(term))
        : rows;

    const renderRows = () => {
        if (!filteredRows.length) {
            return (
                <tr>
                    <td colSpan={TABLE_HEADERS.length} className="py-12 px-2 text-center text-sm text-gray-500">
                        Nenhum roteamento encontrado.
                    </td>
                </tr>
            );
        }
        return filteredRows.map((r, index) => (
            <RoutingRow key={r.id} routing={r} metas={metas} striped={index % 2 === 0} onOpen={() => navigate(`/communication/routing/${r.id}`)} />
        ));
    };

    return (
        <PageList
            pageTitle="Roteamento"
            url=""
            breadcrumb={[
                { label: 'Comunicação', url: '#' },
                { label: 'Roteamento', url: '#' },
            ]}
            tableFieldSort="event"
            tableOrderSort="ASC"
            onTableSort={() => undefined}
            renderRows={renderRows as any}
            page={0}
            pageSize={filteredRows.length || 20}
            onPageChange={() => undefined}
            onItemsPerPageChange={() => undefined}
            itemsLabel="roteamentos"
            defaultBackendHeader={false}
            tableHeaders={TABLE_HEADERS}
            isLoadingData={loading}
            externalTotalElements={filteredRows.length}
            externalTotalPages={1}
        >
            {{
                controlSlot: (
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                        <HiOutlineLockClosed size={14} />
                        Somente leitura. O roteamento é definido pelo time de engenharia e publicado junto com o sistema.
                    </div>
                ),
                filterSlot: (
                    <div className="grid grid-cols-12 gap-1">
                        <PageSearch
                            value={search}
                            label="Procurar por evento ou regra..."
                            onSearch={setSearch}
                            filterCleanVisible
                            filterCleanEnabled={Boolean(search)}
                            onFilterClean={() => setSearch('')}
                            configureVisible={false}
                            className="col-span-12"
                        />
                    </div>
                ),
            }}
        </PageList>
    );
};

export default RoutingPageList;
